import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, ClipboardCopy, MessageCircle, Mail, Pencil, Send } from 'lucide-react';

const STORAGE_KEY = 'socialseo-kaustubh-consult-v1';
const WA_NUMBER = '919252560207';

const STEPS = [
    {
        id: 'outcome',
        section: 'The outcome',
        type: 'long',
        q: 'Twelve months from now, what has this page actually produced for you?',
        hint: 'Not followers. The thing that would make you call it worth it. Paying subscribers on the product, inbound from a specific kind of person, a waitlist you can launch into. Put a number on it if you can.',
        ph: 'By September 2027 this page has...'
    },
    {
        id: 'knownFor',
        section: 'The outcome',
        type: 'short',
        q: 'To get that, what do you need to be known for?',
        hint: 'Finish the sentence: people should think of me when they think of ___. One line. If you are torn between two, pick the one you would still want in three years.',
        ph: 'the guy who...'
    },
    {
        id: 'camp',
        section: 'Why anyone listens',
        type: 'chips',
        q: 'Which one are you on this page right now?',
        hint: 'There is no wrong answer here. Posturing as the expert while you are still the student is what burns trust with an audience, so we would rather build around the truth.',
        options: [
            'The expert. I have done the thing and I teach it.',
            'The student. I am figuring it out in public.',
            'Both, depending on the topic.'
        ]
    },
    {
        id: 'credibility',
        section: 'Why anyone listens',
        type: 'long',
        q: 'Your credibility bank. Everything you have actually done.',
        hint: 'From our call we already have: ten years building products, two products past 100 million users each, a vertical you took to 7 million ARR inside Primetrees, your own startup, early team at two more. Correct anything I got wrong, then add what I missed. Include the failures and what they cost. This is the raw material for every script we write.',
        ph: 'Wins, numbers, things I built, things that went badly...'
    },
    {
        id: 'whoFor',
        section: 'Who this is for',
        type: 'chips',
        q: 'Roughly 80% of your audience today is wannabe founders and 20% are founders. Who should the page be built for from here?',
        hint: 'This is the biggest question on the page, so sit with it. Whoever we optimise around is who you get more of, and your product is priced for small business owners rather than for people who want to become founders.',
        options: [
            'Small business owners in India. The people who would pay for the product.',
            'Founders and operators actively building something.',
            'Wannabe founders. The audience I already have.',
            'That is exactly what I want you to tell me.'
        ]
    },
    {
        id: 'onePerson',
        section: 'Who this is for',
        type: 'long',
        q: 'Describe that one person.',
        hint: 'One real human, not a category. What they run, how many staff, which city, what their day looks like, how they get customers today, what they already pay for. If you have met them, describe the one you remember.',
        ph: 'He runs a...'
    },
    {
        id: 'pains',
        section: 'Who this is for',
        type: 'long',
        q: 'Their painful problems, in their words.',
        hint: 'As many as you can, ten if you can get there. Not the problems you think they should care about. The ones that actually keep them up, annoy them, or embarrass them. Every one of these becomes content later, so quantity helps you here.',
        ph: '1.\n2.\n3.'
    },
    {
        id: 'contrarian',
        section: 'What you believe',
        type: 'fields',
        layout: 'two',
        q: 'What does everyone in your space say that you think is wrong?',
        hint: 'This is the strongest lever you have for standing out. Include the things competitors do, not only what they say. One per line, and pair them across so each belief sits next to the thing it argues with.',
        fields: [
            { id: 'theySay', label: 'What they say or do', ph: 'Everyone says you have to...' },
            { id: 'youBelieve', label: 'What you believe instead', ph: 'I think you actually...' }
        ]
    },
    {
        id: 'associations',
        section: 'What you believe',
        type: 'fields',
        layout: 'quad',
        q: 'Two things you want to be known for. Two you want to be known against.',
        hint: 'What you refuse to be associated with does as much work as what you stand for. Luxury brands are precise about who their product is not for, and it is why the people it is for feel chosen.',
        fields: [
            { id: 'for1', label: 'Known for', ph: 'e.g. shipping fast' },
            { id: 'for2', label: 'Known for', ph: 'e.g. plain english' },
            { id: 'against1', label: 'Known against', ph: 'e.g. hustle theatre' },
            { id: 'against2', label: 'Known against', ph: 'e.g. fake gurus' }
        ]
    },
    {
        id: 'hours',
        section: 'What you can sustain',
        type: 'chips',
        q: 'Honestly, how many hours a week can you give this?',
        hint: 'Answer for your worst week, not your best one. You are doing this alongside a full time role, and a system built for the good weeks quietly dies in month four.',
        options: ['Under 1 hour', '1 to 3 hours', '3 to 6 hours', '6 hours or more']
    },
    {
        id: 'energy',
        section: 'What you can sustain',
        type: 'long',
        q: 'Which part of making content gives you energy, and which part drains you?',
        hint: 'Ideas, writing, recording, editing, posting, replying to comments. Also tell us what time of day your head is sharpest. We hand the draining parts to the system and keep you on the parts you like.',
        ph: 'I enjoy... I hate...'
    },
    {
        id: 'medium',
        section: 'What you can sustain',
        type: 'chips',
        q: 'Your content is AI generated today. If nothing was stopping you, how would you rather show up?',
        hint: 'No judgement either way, and AI stays on the table. It changes what we build, and it changes how fast trust forms with a buyer who is deciding whether to put a card down.',
        options: [
            'On camera, as myself.',
            'My voice, but not my face.',
            'Written, not video.',
            'AI avatar is fine. It is working.'
        ]
    },
    {
        id: 'cadence',
        section: 'What you can sustain',
        type: 'chips',
        q: 'What cadence could you hold for a year without starting to hate it?',
        hint: 'You are at five or six a week today. Volume is easy to raise later and very hard to walk back once your audience expects it, so pick the number you could still hit in a bad month.',
        options: ['2 to 3 a week', '4 to 5 a week', 'Daily', 'Whatever the system can carry without me']
    },
    {
        id: 'human',
        section: 'The 5% that is you',
        type: 'long',
        q: 'Outside of products and business, what are you actually into?',
        hint: 'Sport, music, a city, food, a hobby you are irrationally deep in, the thing you argue about with friends at 1am. The subject you teach is not unique to you, plenty of people teach it. This part is. It is the reason someone picks you over the identical account next to yours.',
        ph: 'I am obsessed with...'
    },
    {
        id: 'creators',
        section: 'What we need to see',
        type: 'long',
        q: 'Which creators in your space do you actually watch, and what do you watch them for?',
        hint: 'One thing to avoid here: do not just list the biggest names. Everybody names the same four. The useful answer is the smaller accounts inside your industry whose work you genuinely rate, the ones we would not find on our own. For each, say which of these it is. You watch them for the information and the value they hand over. You watch them for the expertise, because they have clearly done the thing. Or you watch them for the personality, the way they speak and carry themselves on camera. Those are three different reasons and they lead to three different pages.',
        ph: '@handle, I watch for...\n@handle, I watch for...'
    },
    {
        id: 'links',
        section: 'What we need to see',
        type: 'long',
        q: 'Your links, and your best and worst posts.',
        hint: 'Instagram handle first, then anywhere else you post. Then the 3 that did best and the 3 you were sure would work and did not. We are asking because the gap between those two lists is where most of the strategy is hiding, and we would rather read it than guess at it. One thing though: do not pick the best ones on views. Pick them on what they actually did for you. Which post brought DMs, which one started a real conversation, which one put someone in front of you that you wanted to talk to. A post at 100K views that produced nothing is a worse post than one at 8K that got you three conversations, and we want to know which is which.',
        ph: 'instagram.com/...\n\nBest, and what it actually did:\n\nWorst, and why it surprised me:'
    },
    {
        id: 'anythingElse',
        section: 'What we need to see',
        type: 'long',
        optional: true,
        q: 'Anything else we should know before we start?',
        hint: 'Constraints, a deadline, something your employer would not love, a worry about putting your face out there. Optional, but the awkward things are usually the useful ones.',
        ph: 'Optional'
    }
];

const SECTIONS = STEPS.reduce((acc, s) => (acc.includes(s.section) ? acc : acc.concat(s.section)), []);

function loadSaved() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
}

function valueOf(step, answers) {
    if (step.type === 'fields') {
        return step.fields
            .map((f) => (answers[f.id] || '').trim())
            .filter(Boolean)
            .join('\n');
    }
    return (answers[step.id] || '').trim();
}

// A paired step is only done when both sides of the pair are there. Half a
// contrarian take is not a contrarian take.
function isComplete(step, answers) {
    if (step.type === 'fields') {
        return step.fields.every((f) => (answers[f.id] || '').trim());
    }
    return !!(answers[step.id] || '').trim();
}

// Chip labels are full sentences on screen. Notion wants short option names,
// so the page does the mapping rather than the server guessing at prose.
const TAG_MAP = {
    whoFor: {
        key: 'pageIsFor',
        of: {
            'Small business owners in India. The people who would pay for the product.': 'small business owners',
            'Founders and operators actively building something.': 'founders and operators',
            'Wannabe founders. The audience I already have.': 'wannabe founders',
            'That is exactly what I want you to tell me.': 'you tell me',
        },
    },
    camp: {
        key: 'camp',
        of: {
            'The expert. I have done the thing and I teach it.': 'expert',
            'The student. I am figuring it out in public.': 'student',
            'Both, depending on the topic.': 'both',
        },
    },
    hours: {
        key: 'hoursPerWeek',
        of: {
            'Under 1 hour': 'under 1',
            '1 to 3 hours': '1 to 3',
            '3 to 6 hours': '3 to 6',
            '6 hours or more': '6 or more',
        },
    },
    medium: {
        key: 'medium',
        of: {
            'On camera, as myself.': 'on camera',
            'My voice, but not my face.': 'voice only',
            'Written, not video.': 'written',
            'AI avatar is fine. It is working.': 'ai avatar',
        },
    },
    cadence: {
        key: 'cadence',
        of: {
            '2 to 3 a week': '2 to 3 a week',
            '4 to 5 a week': '4 to 5 a week',
            'Daily': 'daily',
            'Whatever the system can carry without me': 'system carries it',
        },
    },
};

export default function KaustubhQuestionnaire() {
    const [answers, setAnswers] = useState(loadSaved);
    const [step, setStep] = useState(-1); // -1 intro, 0..n-1 questions, n review
    const [copied, setCopied] = useState(false);
    const [focused, setFocused] = useState(false);
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendErr, setSendErr] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        document.title = 'SocialSEO | Kaustubh, before the strategy';
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
        } catch (e) { /* storage blocked, answers stay in memory */ }
    }, [answers]);

    useEffect(() => {
        window.scrollTo(0, 0);
        const t = setTimeout(() => inputRef.current?.focus(), 260);
        return () => clearTimeout(t);
    }, [step]);

    const setAnswer = useCallback((id, v) => setAnswers((prev) => ({ ...prev, [id]: v })), []);

    const answeredCount = useMemo(
        () => STEPS.filter((s) => isComplete(s, answers)).length,
        [answers]
    );

    // Where "resume" should land: the first question still owed. A blank
    // optional is not owed, so it must not stand between him and the send
    // screen. He can still reach it from All or previous.
    const resumeStep = useCallback(() => {
        const i = STEPS.findIndex((s) => !isComplete(s, answers) && !s.optional);
        return i === -1 ? STEPS.length : i;
    }, [answers]);

    const current = step >= 0 && step < STEPS.length ? STEPS[step] : null;
    const filled = current ? isComplete(current, answers) : false;

    const next = useCallback(() => setStep((s) => Math.min(STEPS.length, s + 1)), []);
    const back = useCallback(() => setStep((s) => Math.max(-1, s - 1)), []);

    // Number keys pick chips.
    useEffect(() => {
        if (!current || current.type !== 'chips') return undefined;
        const onKey = (e) => {
            const n = Number(e.key);
            if (n >= 1 && n <= current.options.length) {
                setAnswer(current.id, current.options[n - 1]);
                setTimeout(next, 220);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [current, setAnswer, next]);

    const buildText = () => {
        const lines = ['SocialSEO strategy consult, answers from Kaustubh Khandelwal', ''];
        SECTIONS.forEach((sec) => {
            lines.push('== ' + sec + ' ==', '');
            STEPS.filter((s) => s.section === sec).forEach((s) => {
                lines.push('Q: ' + s.q);
                if (s.type === 'fields') {
                    s.fields.forEach((f) => {
                        lines.push(f.label + ': ' + ((answers[f.id] || '').trim() || '(blank)'));
                    });
                } else {
                    lines.push((answers[s.id] || '').trim() || '(not answered)');
                }
                lines.push('');
            });
        });
        return lines.join('\n');
    };

    // Store to Notion. Best effort by design: if the endpoint is not
    // provisioned or errors, the Copy / WhatsApp / email paths still work, so
    // a server problem never traps his answers in the browser.
    const sendToNotion = useCallback(async () => {
        if (sending || sent) return;
        setSending(true);
        setSendErr(null);
        const tags = {};
        for (const [stepId, m] of Object.entries(TAG_MAP)) {
            const picked = answers[stepId];
            if (picked && m.of[picked]) tags[m.key] = m.of[picked];
        }
        const items = STEPS.map((s) => ({
            section: s.section,
            q: s.q,
            a: valueOf(s, answers),
        }));
        try {
            const r = await fetch('/api/questionnaire', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    form: 'kaustubh-consult',
                    client: 'Kaustubh Khandelwal',
                    source: window.location.href,
                    answered: answeredCount,
                    total: STEPS.length,
                    tags,
                    items,
                }),
            });
            if (r.ok) {
                setSent(true);
            } else {
                // Say it out loud rather than failing quietly. A silent failure
                // here looks like a successful submit and the answers are lost.
                setSendErr(
                    r.status === 503
                        ? 'Saving is not switched on yet at our end. Use WhatsApp or Copy below and we will get it either way.'
                        : 'That did not save. Use WhatsApp or Copy below so it reaches us.'
                );
            }
        } catch (e) {
            setSendErr('No connection. Use WhatsApp or Copy below so it reaches us.');
        }
        setSending(false);
    }, [answers, answeredCount, sending, sent]);

    const copyAnswers = async () => {
        const text = buildText();
        try {
            await navigator.clipboard.writeText(text);
        } catch (e) {
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const waHref = 'https://wa.me/' + WA_NUMBER + '?text=' + encodeURIComponent(buildText());
    const mailHref =
        'mailto:suresh@socialseo.in?subject=' +
        encodeURIComponent('Strategy consult answers, Kaustubh Khandelwal') +
        '&body=' +
        encodeURIComponent(buildText());

    const pct = step < 0 ? 0 : Math.round((Math.min(step + 1, STEPS.length) / STEPS.length) * 100);

    /* ---------- intro ---------- */
    if (step < 0) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 py-24 text-white relative z-10">
                <div className="max-w-[640px] w-full text-center">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-8">
                        Strategy consult · Step 01 of 02
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.03] mb-7 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 pb-2">
                        Kaustubh, before<br />we build the map.
                    </h1>
                    <p className="text-base sm:text-lg text-white/60 leading-relaxed font-light mb-4">
                        Seventeen questions, about twenty minutes. We need all of them answered, so move around freely and come
                        back to the hard ones. We take these plus a full read of your page,
                        spend two days on the research, and bring you a strategy and content structure on the call.
                    </p>
                    <p className="text-sm text-white/40 leading-relaxed font-light mb-10">
                        Answer them yourself. Rough and honest is worth more to us than polished, and the strategy is only ever
                        as good as what goes in. Your answers save in this browser as you type, so you can stop and come back.
                    </p>
                    <button
                        onClick={() => setStep(answeredCount > 0 ? resumeStep() : 0)}
                        className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm"
                    >
                        {resumeStep() === STEPS.length ? 'Review and send' : answeredCount > 0 ? 'Pick up where I left off' : 'Start'}
                        <ArrowRight className="w-4 h-4" />
                    </button>
                    {answeredCount > 0 && (
                        <p className="text-xs text-white/35 mt-5">{answeredCount} of {STEPS.length} already answered</p>
                    )}
                </div>
            </div>
        );
    }

    /* ---------- review ---------- */
    if (step >= STEPS.length) {
        // Measure the encoded wa.me href, because that is the path he will use
        // and the one worth protecting. Note the floor: buildText always emits
        // every question and the section headers, so an untouched form is
        // already ~2,440 characters. Anything at or under that is not a real
        // threshold. 4,000 clears a complete-but-brief set and still catches a
        // long one before WhatsApp starts cutting the prefill off.
        // mailto cannot be saved by any number here, an empty form already
        // exceeds the ~2,048 a Windows handler will take.
        const heavy = waHref.length > 4000;
        // This screen doubles as the index he jumps into from "All", so it has
        // to read correctly when the questionnaire is only half done.
        const missing = STEPS.filter((s) => !isComplete(s, answers) && !s.optional);
        return (
            <div className="min-h-screen px-6 py-24 sm:py-28 text-white relative z-10">
                <div className="max-w-[720px] mx-auto">
                    <div className="text-center mb-14">
                        <div className={`w-14 h-14 rounded-full border flex items-center justify-center mx-auto mb-7 ${missing.length ? 'bg-amber-500/15 border-amber-400/30' : 'bg-emerald-500/15 border-emerald-400/30'}`}>
                            <Check className={`w-6 h-6 ${missing.length ? 'text-amber-300' : 'text-emerald-300'}`} />
                        </div>
                        <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight mb-5">
                            {missing.length ? 'Nearly there.' : 'That is everything.'}
                        </h2>
                        <p className="text-sm sm:text-base text-white/55 max-w-md mx-auto font-light leading-relaxed">
                            {missing.length
                                ? `${missing.length} ${missing.length === 1 ? 'question is' : 'questions are'} still open, marked below. We need all of them, because a gap here is a gap in the strategy we hand you. Tap any one to go straight to it.`
                                : 'Have a last look, fix anything that reads wrong, then send it across. Research starts the same day and we book the call for two days after.'}
                        </p>
                    </div>

                    <div className="space-y-3 mb-12">
                        {STEPS.map((s, i) => {
                            const v = valueOf(s, answers);
                            const done = isComplete(s, answers) || (s.optional && !v);
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => setStep(i)}
                                    className="w-full text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/[0.08] hover:border-white/20 transition p-5 group"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="text-sm font-black text-indigo-400/70 shrink-0 tabular-nums pt-0.5">
                                            {String(i + 1).padStart(2, '0')}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-bold text-white/90 leading-snug mb-1.5">{s.q}</div>
                                            <div className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${done ? 'text-white/50' : 'text-amber-300/60 italic'}`}>
                                                {v || (s.optional ? 'Left blank, that is fine' : 'Not answered yet')}
                                            </div>
                                            {!done && v && (
                                                <div className="text-xs text-amber-300/60 italic mt-1.5">Half done, this one needs both parts</div>
                                            )}
                                        </div>
                                        <Pencil className="w-3.5 h-3.5 text-white/20 group-hover:text-white/50 transition shrink-0 mt-1" />
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Submit is the real one. The other three are the manual
                        fallbacks, kept because they work even when saving does not. */}
                    <div className="flex justify-center mb-4">
                        <button
                            onClick={sendToNotion}
                            disabled={sending || sent}
                            className={`w-full sm:w-auto inline-flex items-center justify-center gap-2.5 font-bold px-9 py-4 rounded-full text-base transition ${
                                sent
                                    ? 'bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 cursor-default'
                                    : sending
                                        ? 'bg-indigo-500/60 text-white/80 cursor-wait'
                                        : 'bg-indigo-500 hover:bg-indigo-400 text-white'
                            }`}
                        >
                            {sent ? <Check className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                            {sent ? 'Submitted' : sending ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>

                    {sent && (
                        <p className="text-sm text-emerald-300/80 text-center mb-6 max-w-md mx-auto leading-relaxed">
                            That is with us. Nothing else needed, we start on the research today.
                        </p>
                    )}
                    {sendErr && (
                        <p className="text-sm text-amber-300/80 text-center mb-6 max-w-md mx-auto leading-relaxed">
                            {sendErr}
                        </p>
                    )}
                    {!sent && !sendErr && (
                        <p className="text-xs text-white/35 text-center mb-6">
                            Or send it across yourself
                        </p>
                    )}

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                            onClick={copyAnswers}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 transition text-white font-bold px-7 py-3.5 rounded-full text-sm"
                        >
                            {copied ? <Check className="w-4 h-4" /> : <ClipboardCopy className="w-4 h-4" />}
                            {copied ? 'Copied' : 'Copy everything'}
                        </button>
                        <a
                            href={waHref}
                            target="_blank"
                            rel="noreferrer"
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 transition text-white font-bold px-7 py-3.5 rounded-full text-sm"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Send on WhatsApp
                        </a>
                        <a
                            href={mailHref}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 transition text-white font-bold px-7 py-3.5 rounded-full text-sm"
                        >
                            <Mail className="w-4 h-4" />
                            Email it
                        </a>
                    </div>
                    {heavy && (
                        <p className="text-xs text-amber-300/60 text-center mt-6 max-w-md mx-auto leading-relaxed">
                            You have written a lot, which is good. Use Copy and paste it into WhatsApp yourself,
                            because a prefilled link this long gets cut short by both WhatsApp and email.
                        </p>
                    )}
                    <p className="text-xs text-white/30 text-center mt-7">
                        {answeredCount} of {STEPS.length} answered.{' '}
                        {sent
                            ? 'Saved at our end.'
                            : 'Your answers stay in this browser until you submit.'}
                    </p>
                </div>
            </div>
        );
    }

    /* ---------- a question ---------- */
    const shortVal = answers[current.id] || '';
    // Shrink by font-size, not transform, so a long answer still fills the
    // width of a phone instead of collapsing into the left third of it.
    const f = shortVal.length <= 12 ? 1 : Math.max(0.42, 1 - (shortVal.length - 12) * 0.018);
    const shortSize = `clamp(1.15rem, ${(8.5 * f).toFixed(2)}vw, ${(3.75 * f).toFixed(2)}rem)`;

    return (
        <div className="min-h-screen flex flex-col text-white relative z-10">

            {/* top chrome */}
            <div className="fixed top-0 left-0 right-0 z-50 px-5 sm:px-8 pt-5 sm:pt-6 pointer-events-none">
                <div className="max-w-[880px] mx-auto flex items-center gap-4">
                    <button
                        onClick={back}
                        aria-label="Back"
                        className="pointer-events-auto w-9 h-9 rounded-full bg-white/10 border border-white/10 hover:bg-white/20 transition flex items-center justify-center shrink-0"
                    >
                        <ArrowLeft className="w-4 h-4 text-white/70" />
                    </button>
                    <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div
                            className="h-full bg-indigo-400 rounded-full transition-all duration-500 ease-out"
                            style={{ width: pct + '%' }}
                        />
                    </div>
                    <div className="text-[10px] sm:text-xs font-bold tracking-widest text-white/40 tabular-nums shrink-0">
                        {String(step + 1).padStart(2, '0')} / {STEPS.length}
                    </div>
                    <button
                        onClick={() => setStep(STEPS.length)}
                        className="pointer-events-auto shrink-0 text-[10px] sm:text-xs font-bold tracking-widest uppercase text-white/40 hover:text-white/80 transition border border-white/15 hover:border-white/35 rounded-full px-3 py-1.5"
                    >
                        All
                    </button>
                </div>
            </div>

            <div className="flex-1 flex items-center px-6 py-28 sm:py-32">
                <div key={step} className="max-w-[880px] mx-auto w-full animate-[qin_.45s_cubic-bezier(.16,1,.3,1)_both]">

                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-6">
                        {current.section}
                    </div>

                    <h2 className={`font-black tracking-tight leading-[1.1] mb-4 ${current.type === 'short' ? 'text-2xl sm:text-3xl text-white/70' : 'text-2xl sm:text-4xl'}`}>
                        {current.q}
                    </h2>

                    <p className="text-sm sm:text-base text-white/45 font-light leading-relaxed max-w-[680px] mb-9">
                        {current.hint}
                    </p>

                    {current.type === 'short' && (
                        <div
                            className="cursor-text mb-2 min-h-[72px] flex items-center relative"
                            onClick={() => inputRef.current?.focus()}
                        >
                            <input
                                ref={inputRef}
                                type="text"
                                value={shortVal}
                                onChange={(e) => setAnswer(current.id, e.target.value)}
                                onFocus={() => setFocused(true)}
                                onBlur={() => setFocused(false)}
                                onKeyDown={(e) => { if (e.key === 'Enter') next(); }}
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                className="absolute inset-0 w-full h-full opacity-0 bg-transparent border-0 outline-none"
                                aria-label={current.q}
                            />
                            <div
                                className="w-full font-black tracking-tight leading-[1.08] break-words transition-[font-size] duration-200"
                                style={{ fontSize: shortSize }}
                            >
                                <span className={shortVal ? 'text-white' : 'text-white/25'}>
                                    {shortVal || current.ph}
                                </span>
                                <span
                                    className={`inline-block w-[3px] h-[0.85em] align-middle ml-1 bg-indigo-400 ${focused ? 'animate-pulse' : 'opacity-0'}`}
                                />
                            </div>
                        </div>
                    )}

                    {current.type === 'long' && (
                        <textarea
                            ref={inputRef}
                            rows={7}
                            value={answers[current.id] || ''}
                            onChange={(e) => setAnswer(current.id, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) next(); }}
                            placeholder={current.ph}
                            className="w-full rounded-2xl bg-black/30 border border-white/10 focus:border-indigo-400/60 focus:outline-none px-5 py-4 text-base text-white placeholder-white/20 resize-y leading-relaxed"
                        />
                    )}

                    {current.type === 'chips' && (
                        <div className="grid gap-3 sm:grid-cols-2">
                            {current.options.map((o, i) => {
                                const sel = answers[current.id] === o;
                                return (
                                    <button
                                        key={o}
                                        onClick={() => { setAnswer(current.id, o); setTimeout(next, 220); }}
                                        className={`text-left rounded-2xl border px-5 py-4 transition flex items-start gap-3.5 ${sel ? 'bg-indigo-500/20 border-indigo-400/60' : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/25'}`}
                                    >
                                        <span className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-black tabular-nums mt-0.5 ${sel ? 'bg-indigo-400 text-slate-900' : 'bg-white/10 text-white/50'}`}>
                                            {sel ? <Check className="w-3.5 h-3.5" /> : i + 1}
                                        </span>
                                        <span className="text-sm sm:text-[15px] font-semibold text-white/90 leading-snug">{o}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {current.type === 'fields' && (
                        <div className="grid gap-4 sm:grid-cols-2">
                            {current.fields.map((f, i) => (
                                <div key={f.id}>
                                    <div className={`text-[10px] font-bold tracking-[0.2em] uppercase mb-2.5 ${f.label === 'Known against' ? 'text-rose-300/70' : 'text-emerald-300/70'}`}>
                                        {f.label}
                                    </div>
                                    {current.layout === 'two' ? (
                                        <textarea
                                            ref={i === 0 ? inputRef : null}
                                            rows={7}
                                            value={answers[f.id] || ''}
                                            onChange={(e) => setAnswer(f.id, e.target.value)}
                                            placeholder={f.ph}
                                            className="w-full rounded-2xl bg-black/30 border border-white/10 focus:border-indigo-400/60 focus:outline-none px-5 py-4 text-sm text-white placeholder-white/20 resize-y leading-relaxed"
                                        />
                                    ) : (
                                        <input
                                            ref={i === 0 ? inputRef : null}
                                            type="text"
                                            value={answers[f.id] || ''}
                                            onChange={(e) => setAnswer(f.id, e.target.value)}
                                            placeholder={f.ph}
                                            className="w-full rounded-xl bg-black/30 border border-white/10 focus:border-indigo-400/60 focus:outline-none px-4 py-3.5 text-sm text-white placeholder-white/20"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Move freely in both directions, but every question is
                        needed, so an unanswered one advances as "come back to
                        this" rather than as a skip. */}
                    <div className="flex items-center gap-5 mt-9">
                        <button
                            onClick={next}
                            className={`inline-flex items-center gap-2 font-bold px-7 py-3.5 rounded-full text-sm transition ${filled || current.optional ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'bg-transparent border border-white/25 text-white/60 hover:text-white hover:border-white/50'}`}
                        >
                            {filled || current.optional
                                ? (step === STEPS.length - 1 ? 'Review my answers' : 'Next')
                                : 'Come back to this one'}
                            <ArrowRight className="w-4 h-4" />
                        </button>
                        {step > 0 && (
                            <button onClick={back} className="text-xs text-white/35 hover:text-white/70 transition">
                                previous
                            </button>
                        )}
                        {current.type === 'long' && filled && (
                            <span className="text-[11px] text-white/25 hidden sm:inline">cmd + enter</span>
                        )}
                    </div>

                </div>
            </div>

            <style>{`
                @keyframes qin {
                    from { opacity: 0; transform: translateY(14px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
